import { useEffect, useState } from 'react';
import {
  ButtonSecondary,
  PaymentType,
  SidePanel,
  SidePanelType,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearTopUpInstruction } from '../store/slices/top-up';
import OverviewTopupSidepanel from '../../../components/partial/overview-topup/overview-topup-sidepanel';
import { CustomerType, PspPartnerPaymentType } from '../types';
import {
  useLazyGetCustomersTopUpInstructionsQuery,
  useLazyGetCustomersTopUpInstructionPdfQuery,
  useLazyGetCustomersTopUpQuery,
} from '../store/services/customers';
import { TopupPanelTypes } from './types';

const getPspPartnerPaymentType = (type: string) => {
  let typeName;

  switch (type) {
    case PspPartnerPaymentType.BACS:
      typeName = 'BACS';
      break;
    case PspPartnerPaymentType.FASTER_PAYMENT:
      typeName = 'Faster payment';
      break;
    case PspPartnerPaymentType.SEPA_SCT:
      typeName = 'SEPA SCT';
      break;
    case PspPartnerPaymentType.SEPA_SCT_INST:
      typeName = 'SEPA SCT INST';
      break;
    case PspPartnerPaymentType.SWIFT:
      typeName = 'SWIFT';
      break;
    case PspPartnerPaymentType.TARGET_2:
      typeName = 'Target 2';
      break;
    case PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT:
      typeName = 'Crypto Asset Payment';
      break;
    case PspPartnerPaymentType.FEDWIRE:
      typeName = 'Fedwire';
      break;
    case PspPartnerPaymentType.CASH_OVER_COUNTER:
      typeName = 'Cash Over Counter';
      break;
    default:
      typeName = type;
      break;
  }

  return typeName;
};

const TopUpPanel = ({
  topUpPanelIsOpen,
  setTopUpPanelIsOpen,
  type,
  selectedTopUpId,
  setSelectedTopUpId,
  userAccount,
  userAccounts,
  setUserAccount,
}: TopupPanelTypes) => {
  const [topUpCurrency, setTopUpCurrency] = useState('');
  const [topUpAccount, setTopUpAccount] = useState('');
  const [selectedInstructionType, setInstructionType] = useState('');
  const [topUpOptions, setTopUpOptions] = useState<
    {
      companyName: string;
      id: string;
      visibleToClients: boolean;
    }[]
  >([]);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [getTopUp, { isLoading, isFetching }] = useLazyGetCustomersTopUpQuery();

  const prevSearchText = usePrevious(searchText);

  const { topUpInstrusctions } = useAppSelector((state) => state.topUpPanel);
  const {
    topUpOptions: individualTopUpOptions,
    topUpOptionsPagination: individualTopUpOptionsPagination,
  } = useAppSelector((state) => state.individualCustomers.info.general);
  const {
    topUpOptions: corporateTopUpOptions,
    topUpOptionsPagination: corporateTopUpOptionsPagination,
  } = useAppSelector((state) => state.corporateCustomers.info.general);
  const { currencies } = useAppSelector((state) => state.customersDictionaries);

  const [
    getCustomersTopUpInstructions,
    { isLoading: instructionsIsLoading, isFetching: instructionsIsFetching },
  ] = useLazyGetCustomersTopUpInstructionsQuery();
  const [
    getCustomersTopUpInstructionPdf,
    { isLoading: pdfDownloadInProgress },
  ] = useLazyGetCustomersTopUpInstructionPdfQuery();

  const onSearchTopUp = async ({
    text,
    cursor,
  }: {
    cursor?: string;
    text?: string;
  }) => {
    const body: { companyName?: string; cursor?: string } = {
      companyName: text || '',
      cursor: cursor || '',
    };

    if (cursor) {
      setNextPageIsLoading(true);
    }

    const response = await getTopUp(body);

    if (response && response.data && response.data.data) {
      if (text) {
        setTopUpOptions(response.data.data?.edges?.map((item) => item.node));
      } else if (!text && prevSearchText) {
        setTopUpOptions(
          type === CustomerType.INDIVIDUAL
            ? (individualTopUpOptions as {
                companyName: string;
                id: string;
                visibleToClients: boolean;
              }[])
            : (corporateTopUpOptions as {
                companyName: string;
                id: string;
                visibleToClients: boolean;
              }[]),
        );
      }

      setNextPageIsLoading(false);
    }
  };

  const dispatch = useAppDispatch();

  const getCustomersTopUpInstructionPdfAction = async (
    index: number,
    instructionType: PspPartnerPaymentType,
  ) => {
    setInstructionType(instructionType);

    const response = await getCustomersTopUpInstructionPdf({
      id: topUpAccount,
      pspPartnerId: selectedTopUpId as string,
      currencyId: topUpCurrency,
      index,
    });

    let currentInstruction;

    if (topUpInstrusctions) {
      currentInstruction = topUpInstrusctions?.items[index];
    }

    if (response.data) {
      const currentAccount = userAccounts?.find(
        (account) => account.accountId === topUpAccount,
      );

      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${
        currentInstruction &&
        currentInstruction.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT
          ? `${currentInstruction?.casp?.name} Payment`
          : getPspPartnerPaymentType(instructionType)
      }(${currentAccount?.account.fullNumber}).pdf`;
      hiddenElement.click();

      setInstructionType('');
    }
  };

  const handleCloseTopUpSidepanel = () => {
    setTopUpAccount('');
    setSelectedTopUpId('');
    setTopUpCurrency('');
    setTopUpPanelIsOpen(false);
    setTopUpOptions([]);
    setSearchText('');
    setNextPageIsLoading(false);

    dispatch(clearTopUpInstruction());

    if (setUserAccount) {
      setUserAccount('');
    }
  };

  useEffect(() => {
    if (topUpPanelIsOpen) {
      if (type === CustomerType.INDIVIDUAL && individualTopUpOptions) {
        setTopUpOptions(individualTopUpOptions);
      } else if (type === CustomerType.CORPORATE && corporateTopUpOptions) {
        setTopUpOptions(corporateTopUpOptions);
      }
    }
  }, [type, individualTopUpOptions, corporateTopUpOptions, topUpPanelIsOpen]);

  useEffect(() => {
    if (topUpAccount && selectedTopUpId && topUpCurrency) {
      getCustomersTopUpInstructions({
        id: topUpAccount,
        pspPartnerId: selectedTopUpId,
        currencyId: topUpCurrency,
      });
    }
  }, [topUpAccount, selectedTopUpId, topUpCurrency]);

  useEffect(() => {
    if (userAccount) {
      setTopUpAccount(userAccount);
    }
  }, [userAccount]);

  return (
    <SidePanel
      type={SidePanelType.Customers}
      closeOnClickOutside
      isOpen={topUpPanelIsOpen}
      onClose={handleCloseTopUpSidepanel}
      title="Download Top-up options"
      footer={
        <ButtonSecondary
          text="Cancel"
          onClick={handleCloseTopUpSidepanel}
          className="button-white"
          bold
        />
      }
      sidepanelExpandedKey="customers-accounts-statements"
    >
      <OverviewTopupSidepanel
        selectedTopUpId={selectedTopUpId}
        setSelectedTopUpId={setSelectedTopUpId}
        topUpOptions={topUpOptions}
        topUpCurrency={topUpCurrency}
        setTopUpCurrency={setTopUpCurrency}
        currencies={currencies}
        topUpAccount={topUpAccount}
        setTopUpAccount={setTopUpAccount}
        topUpAccounts={userAccounts}
        instructionsIsLoading={instructionsIsLoading || instructionsIsFetching}
        topUpInstrusctions={topUpInstrusctions}
        getTopUpInstructionPdf={getCustomersTopUpInstructionPdfAction}
        pdfDownloadInProgress={pdfDownloadInProgress}
        selectedInstructionType={selectedInstructionType}
        onSearchTopUp={onSearchTopUp}
        searchInProgress={isLoading || isFetching}
        nextPageIsLoading={nextPageIsLoading}
        searchText={searchText}
        setSearchText={setSearchText}
        topUpOptionsPagination={
          type === CustomerType.INDIVIDUAL
            ? individualTopUpOptionsPagination
            : corporateTopUpOptionsPagination
        }
      />
    </SidePanel>
  );
};

export default TopUpPanel;
